/**
 * PRODUCT TYPE → DATABASE MAPPING (Single Source of Truth)
 *
 * Each product_type has a default local postgres database (if available).
 * This is the "builder's database" — read-only for intelligence.
 *
 * Hierarchy:
 *   1. Client's own DB (client_prod) — configured during onboarding
 *   2. Product type's default DB (prod) — local postgres on EC2
 *   3. No DB (demo) — hardcoded demo responses only
 *
 * When a client launches, the system checks:
 *   - Does the client have db_config? → green dot "client_prod"
 *   - Does the product_type have a default DB? → green dot "prod"
 *   - Neither? → orange dot "demo"
 *
 * CANONICAL vs SLUG identifiers:
 *   Some product_type keys (e.g. the TN procurement prototype) carry a long
 *   canonical string that is LLM-facing and user-facing verbatim — it is
 *   injected into system prompts and CID-DDL intelligence so the model
 *   understands the scope. Those canonical strings contain spaces and
 *   parentheses, which are invalid in URLs, AWS resource names, RAG
 *   namespaces, and some config systems. For those contexts we carry a
 *   derived URL/path-safe slug alongside the canonical key.
 *
 *   Use `resolveProductType(input)` at any boundary where input may arrive
 *   as either form — it returns the canonical key, or null if unknown.
 */

// ---- Canonical ProductType identifiers (LLM-facing and user-facing) ----
export const PRODUCT_TYPE_CANONICAL_TN_PROCUREMENT =
  'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)';

export const PRODUCT_TYPE_CANONICAL_CHINA_US_INTEL =
  'China-US-Strategic-Intel.Type-1';

export const PRODUCT_TYPE_CANONICAL_INDUSTRIAL_WATER_PFAS =
  'IndustrialWater-PFAS-Intelligence.Type-2';

export const PRODUCT_TYPE_CANONICAL_BIOPHARMA_EXECUTIVE =
  'BioPharma-Executive-Intelligence.Type-2';

// ---- URL/path/namespace-safe slugs (never shown to users or LLM) ----
export const PRODUCT_TYPE_SLUG_TN_PROCUREMENT =
  'procurement-intelligence-type-1-tn-prototype';

export const PRODUCT_TYPE_SLUG_CHINA_US_INTEL =
  'china-us-intel';

export const PRODUCT_TYPE_SLUG_INDUSTRIAL_WATER_PFAS =
  'industrial-water-pfas-intelligence-type-1';

export const PRODUCT_TYPE_SLUG_BIOPHARMA_EXECUTIVE =
  'biopharma-executive-intelligence-type-2';

const PRODUCT_DB_MAP = {
  'Restaurant.Type-1': {
    db: 'restaurant-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 15,
    description: 'Restaurant operations — orders, inventory, reservations, staff, catering',
  },
  'Gym.Type-1': {
    db: 'gym-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 15,
    description: 'Gym operations — members, classes, equipment, payments, check-ins',
  },
  // All others: no local database yet — demo only
  'Financial-Bank-Type1': {
    db: 'fin-bank-qrm-light-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 11,
    description: 'PQRM-grade financial bank — assets, liabilities, cashflows, risk metrics, balance sheet',
  },
  'Financial-Bank.Type-1': {
    db: 'fin-bank-qrm-light-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 11,
    description: 'PQRM-grade financial bank — assets, liabilities, cashflows, risk metrics, balance sheet',
  },
  'RealEstate.Type-1': {
    db: 'realestate-type1-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 12,
    description: 'Real estate operations — properties, listings, agents, transactions, leads, rentals',
  },
  'Insurance.Type-1': {
    db: 'insurance-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 12,
    description: 'Insurance operations — policies, premiums, claims, agents, offices, renewals, underwriting, loss ratios',
  },
  'HVAC.Type-1': {
    db: 'hvac-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'HVAC service operations — service calls, technicians, parts inventory, maintenance contracts, warranties, customers',
  },
  'Lawn.Type-1': {
    db: 'lawn-landscape-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'Lawn & landscape operations — jobs, crews, customers, equipment, zones, seasonal scheduling, invoices',
  },
  'Plumbing.Type-1': {
    db: 'plumbing-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'Plumbing network operations — work orders, locations, franchise fees, leads, customer ratings, job volume',
  },
  'Medical.Type-1': {
    db: 'medical-wellness-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 12,
    description: 'Medical & wellness practice — patients, appointments, treatments, billing, insurance claims, supplies, rooms',
  },
  'Salon.Type-1': {
    db: 'salon-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 8,
    description: 'Salon operations — appointments, stylists, services, products, customers, memberships, revenue',
  },
  'IT-Consult.Type-100': {
    db: 'it-consult-100-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'IT consulting (100 emp) — projects, clients, timesheets, billing, resources, utilization',
  },
  'IT-Consult.Type-500': {
    db: 'it-consult-500-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 14,
    description: 'IT consulting (500 emp) — projects, clients, timesheets, billing, resources, departments, utilization',
  },
  'IT-Consult.Type-10000': {
    db: 'it-consult-10k-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 18,
    description: 'IT consulting (10K emp) — projects, clients, timesheets, billing, resources, departments, divisions, utilization, bench',
  },
  'Grocery.Type-1': {
    db: 'supermarket-n-restaurant-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 20,
    description: 'Supermarket & eat-in/takeaway restaurant — products, inventory, menu, kitchen, orders, payments, loyalty, analytics',
  },
  'Supermarket.Type-1': {
    db: 'supermarket-n-restaurant-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 20,
    description: 'Supermarket & eat-in/takeaway restaurant — products, inventory, menu, kitchen, orders, payments, loyalty, analytics',
  },
  'PressureWashing-ExteriorCleaning-Intelligence.Type-1': {
    db: 'lancaster-prowash-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 20,
    description: 'Pressure washing & exterior cleaning — services, customers, quotes, work orders, crews, equipment, weather, CEO directives, AI opportunities',
    rag_namespaces: [
      'lancaster-prowash-federal-policies',
      'lancaster-prowash-state-policies',
      'lancaster-prowash-ceo-directives',
      'lancaster-prowash-preferences',
    ],
  },
  'Logistics.Type-1': {
    db: 'logistics-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'Logistics & freight operations — routes, vehicles, drivers, deliveries, fuel, maintenance, SLA tracking',
  },
  'Valet.Type-1': {
    db: 'valet-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 8,
    description: 'Valet & auto services — valet tickets, shifts, EV charging, fleet vehicles, retrieval times, detail services',
  },
  'Education.Type-1': {
    db: 'education-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 10,
    description: 'Education operations — students, courses, enrollments, instructors, assessments, attendance, billing',
  },
  'Healthcare-ClinicalOps.Type-1': {
    db: 'healthcare-clinical-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 20,
    description: 'Hospital & clinical operations — appointments, encounters, patients, memberships, claims, referrals, inventory, HIPAA policies, audit',
  },
  'ASBI.Type-1': {
    db: 'asbi-platform-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 6,
    description: 'Platform intelligence — subscriber activity, query logs, MRR, churn, engagement',
  },
  // Full canonical ProductType string is the key itself — it is used verbatim
  // in the signup UI, the client dashboard header, CID-DDL intelligence
  // injection, and LLM system prompts so the model scopes reasoning to
  // Tamil Nadu state government procurement rather than generic worldwide
  // procurement. The slug is the URL/path/namespace-safe derivative.
  [PRODUCT_TYPE_CANONICAL_TN_PROCUREMENT]: {
    slug: PRODUCT_TYPE_SLUG_TN_PROCUREMENT,
    llm_product: 'ProcureGuard-LLM',
    db: 'sample-tn-procurement-db',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 25,
    description:
      'Government procurement intelligence (Tamil Nadu prototype) — tenders, bids, contracts, vendor risk, policy precedence, CM directives, district analytics',
    rag_namespaces: [
      'tn-procurement-central',
      'tn-procurement-state',
      'tn-procurement-department',
      'tn-procurement-cm-directive',
    ],
  },
  // Product Type #12 — Geopolitical Decision Intelligence for Executives
  // Transforms China-US events into personalized commercial meaning via
  // CID-DDLS injection (8-layer architecture). The slug is used for REST
  // paths, RAG namespaces, and S3 prefixes.
  [PRODUCT_TYPE_CANONICAL_CHINA_US_INTEL]: {
    slug: PRODUCT_TYPE_SLUG_CHINA_US_INTEL,
    llm_product: 'GeoPulse-LLM',
    db: 'china-us-intel-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 7,
    description:
      'Geopolitical business intelligence (executive subscription) — events, corporate impacts, subscriber signals, period analytics, forecasts, sector weights, subscriber profiles',
    rag_namespaces: [
      'china-us-trade-policy',
      'china-us-tech-controls',
      'china-us-corporate-filings',
      'china-us-historical',
    ],
  },
  // Product Type — IndustrialWater-PFAS-Intelligence.Type-2
  // AI-driven decision intelligence layer for industrial water operations:
  // PFAS regulatory intelligence, membrane operations optimization, and
  // executive decision intelligence. The reference tenant is the synthetic
  // Empirical-AquaPilot Industries — used for demos and pilots without
  // referencing any real water technology company. The canonical
  // ProductType string is injected verbatim into LLM system prompts, RAG
  // namespaces, and audit logs; the slug is the URL/path-safe derivative.
  [PRODUCT_TYPE_CANONICAL_INDUSTRIAL_WATER_PFAS]: {
    slug: PRODUCT_TYPE_SLUG_INDUSTRIAL_WATER_PFAS,
    llm_product: 'WaterIntel-LLM',
    displayName: 'IndustrialWater-PFAS-Intelligence.Type-2',
    tier: 2,
    pricingMonthly: 1000,
    db: 'sample-industrial-water-db',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 22,
    description:
      'Industrial water and membrane technology — PFAS regulatory intelligence, membrane performance, water quality, compliance assessments, agentic decision log; reference tenant Empirical-AquaPilot Industries',
    rag_namespaces: [
      'industrial-water-federal-epa',
      'industrial-water-state-nc-deq',
      'industrial-water-ceo-directives',
      'industrial-water-lob-policies',
    ],
  },
  // Direct key alias for IndustrialWater-PFAS — stored by subscribers as this exact string
  'IndustrialWater-PFAS-Intelligence.Type-2': {
    slug: PRODUCT_TYPE_SLUG_INDUSTRIAL_WATER_PFAS,
    llm_product: 'WaterIntel-LLM',
    displayName: 'IndustrialWater-PFAS-Intelligence.Type-2',
    tier: 2,
    pricingMonthly: 1000,
    db: 'sample-industrial-water-db',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 22,
    description:
      'Industrial water and membrane technology — PFAS regulatory intelligence, membrane performance, water quality, compliance assessments, agentic decision log; reference tenant Empirical-AquaPilot Industries',
    rag_namespaces: [
      'industrial-water-federal-epa',
      'industrial-water-state-nc-deq',
      'industrial-water-ceo-directives',
      'industrial-water-lob-policies',
    ],
  },
  // Executive Scientific + Commercial Decision Intelligence Platform for
  // US-based large biopharma enterprises. 6-agent agentic pipeline transforms
  // competitor/regulatory events into compliance-verified executive artifacts
  // with 21 CFR Part 11 audit trails. Cost-optimized: Gemini Flash extraction
  // ($0.01/trigger), Python scoring ($0), Claude/Gemini Pro drafting ($0.05-0.10).
  [PRODUCT_TYPE_CANONICAL_BIOPHARMA_EXECUTIVE]: {
    slug: PRODUCT_TYPE_SLUG_BIOPHARMA_EXECUTIVE,
    llm_product: 'BioSphere-LLM',
    displayName: 'BioPharma Executive Intelligence',
    tier: 2,
    pricingMonthly: 4999,
    db: 'pnp_platform',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 7,
    description:
      'BioPharma executive intelligence — competitive response, regulatory monitoring, pipeline risk, artifact generation, policy-aware reasoning, 21 CFR Part 11 audit trail, PV escalation',
    rag_namespaces: [
      'biopharma-federal-fda',
      'biopharma-state-overlay',
      'biopharma-ceo-directives',
      'biopharma-operational-preferences',
    ],
  },
  // Product Type — Appliances-Maintenance-Repair.Type-1
  // Field Service Flat-Rate Repair intelligence for small business appliance
  // repair companies. Flat-rate $150 labor + parts, $85 diagnostic (declined),
  // $0 beyond scope. Sub-LOBs: Kitchen, Laundry, HVAC, Water Heaters,
  // Preventive Maintenance, Emergency Repair.
  'Appliances-Maintenance-Repair.Type-1': {
    llm_product: 'ApplianceRepair-LLM',
    displayName: 'Appliance Maintenance & Repair Intelligence',
    db: 'appliance-repair-ops-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 7,
    description:
      'Appliance maintenance & repair field service — customers, technicians, service catalog, work orders, parts inventory, revenue analytics, maintenance contracts',
    rag_namespaces: [
      'appliance-repair-federal-policies',
      'appliance-repair-state-policies',
      'appliance-repair-ceo-directives',
      'appliance-repair-lob-directives',
    ],
  },
  // Product Type — FinancialBank.ProcessOwnerPortal.Type-1
  // Process Owner Portal Intelligence dashboard for GenAI readiness scoring,
  // metadata governance, and AI enablement gating for Financial Bank process
  // and operations management. Uses pnp_bai_pop schema in the platform DB.
  'FinancialBank.ProcessOwnerPortal.Type-1': {
    llm_product: 'POP-GenAI-Readiness',
    displayName: 'Process Owner Portal Intelligence.Type-1',
    tier: 1,
    pricingMonthly: 999,
    db: 'process_owner_portal_db',
    schema: 'pnp_bai_pop',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 25,
    description:
      'Process Owner Portal GenAI Readiness Intelligence — process domains, Oracle metadata catalog, AI readiness scoring (6 dimensions), CEO guardrail enforcement, semantic layer, DQ execution, executive dashboards',
    rag_namespaces: [
      'pop-federal-policies',
      'pop-ceo-directives',
      'pop-lob-directives',
      'pop-preferences',
    ],
  },
  // Alias — App.js product selector uses the shorter key
  'ProcessOwnerPortal.Type-1': {
    llm_product: 'POP-GenAI-Readiness',
    displayName: 'Process Owner Portal Intelligence.Type-1',
    tier: 1,
    pricingMonthly: 999,
    db: 'process_owner_portal_db',
    schema: 'pnp_bai_pop',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 25,
    description:
      'Process Owner Portal GenAI Readiness Intelligence — process domains, Oracle metadata catalog, AI readiness scoring (6 dimensions), CEO guardrail enforcement, semantic layer, DQ execution, executive dashboards',
    rag_namespaces: [
      'pop-federal-policies',
      'pop-ceo-directives',
      'pop-lob-directives',
      'pop-preferences',
    ],
  },
  // Product Type — PowerQuality-Engineering-Intelligence.Type-1
  // Full vertical-slice deployment for Sttar Engineering Services — a Tamil
  // Nadu-based power quality and electrical panel manufacturing company.
  // Covers APFCR panels, AMF panels, servo stabilizers, harmonic filters,
  // energy audits, compliance, inventory, and field operations.
  'PowerQuality-Engineering-Intelligence.Type-1': {
    llm_product: 'PowerGrid-LLM',
    db: 'sttar-powerquality-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 20,
    description:
      'Power quality engineering — APFCR, AMF, servo, harmonics, energy audits, compliance, inventory, field ops',
    rag_namespaces: [
      'sttar-powerquality-federal-policies',
      'sttar-powerquality-state-policies',
      'sttar-powerquality-ceo-directives',
      'sttar-powerquality-preferences',
    ],
  },
  // Product Type — Manufacturing.FoodBeverage.PredictiveMaintenance.Type-1
  // Plant Asset Reliability Intelligence for Food & Beverage manufacturing.
  // 8-agent agentic pipeline: Telemetry Ingestion → Anomaly Detection → Failure
  // Prediction → Priority Scheduling → Work Order Generation → Policy Verification
  // → Human Approval → ERP/CMMS Dispatch. Gemini Flash for telemetry parsing,
  // ChatGPT-4o for structured work orders and agentic ERP routing.
  // OSHA/FDA/EPA/DOE compliant with LOTO checklists and immutable audit trail.
  'Manufacturing.FoodBeverage.PredictiveMaintenance.Type-1': {
    slug: 'manufacturing-fb-predictive-maintenance',
    llm_product: 'PlantReliability-LLM',
    displayName: 'Manufacturing F&B Predictive Maintenance Intelligence',
    tier: 1,
    pricingMonthly: 1499,
    db: 'manufacturing-fb-predictive-maint-v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 8,
    description:
      'Plant asset reliability intelligence — sensor telemetry, anomaly detection, failure prediction (14-day RUL), priority scheduling, work orders with LOTO compliance, hierarchical policy verification, human approval, ERP/CMMS dispatch',
    rag_namespaces: [
      'manufacturing-fb-federal-policies',
      'manufacturing-fb-ceo-directives',
      'manufacturing-fb-lob-directives',
      'manufacturing-fb-sublob-directives',
    ],
  },
  // Product Type — energy-evcharge-ops-v1
  // EV Charging Revenue & Reliability Intelligence.Type-1 for Charge Point
  // Operators (CPOs), fleet operators, and municipal EV charging networks.
  // Monitors OCPP-based infrastructure for Asset Health Scores, Revenue
  // Leakage detection (ghost outages), and Evidence Bundles for maintenance
  // prioritization. 14 domain tables + 5 platform tables in PostgreSQL.
  // Strictly Type-1: scoring, dashboards, recommendations, evidence delivery.
  'energy-evcharge-ops-v1': {
    llm_product: 'EVCharge-LLM',
    displayName: 'EV Charging Revenue & Reliability Intelligence.Type-1',
    db: 'pnp_evcharge_analytics',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_engine',
    tables: 14,
    description:
      'EV charging network performance monitoring, dynamic tariff revenue intelligence, OCPP session analytics, and asset health scoring',
    rag_namespaces: [
      'ocpp-protocols',
      'station-specifications',
      'regional-grid-tariffs',
      'preventive-maintenance-baselines',
    ],
  },
  // Product Type — utilities-grid-reliability-v1
  // Grid Reliability & Wildfire Mitigation Intelligence.Type-1 for regulated
  // US electric utilities (IOUs, municipals, cooperatives). Monitors grid
  // infrastructure for reliability risk scores, wildfire exposure rankings,
  // vegetation management compliance, transformer health, SAIDI/SAIFI
  // trending, and evidence bundle generation for PUC filings.
  // Strictly Type-1: scoring, dashboards, recommendations, evidence delivery.
  'utilities-grid-reliability-v1': {
    llm_product: 'GridReliability-LLM',
    displayName: 'Grid Reliability & Wildfire Mitigation Intelligence.Type-1',
    db: 'pnp_grid_reliability',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_engine',
    tables: 22,
    description:
      'Grid reliability scoring, wildfire risk intelligence, vegetation compliance, transformer health, SAIDI/SAIFI trending, and evidence bundle generation for regulated US utilities',
    rag_namespaces: [
      'utilities-grid-reliability-federal-policies',
      'utilities-grid-reliability-ceo-directives',
      'utilities-grid-reliability-lob-directives',
      'utilities-grid-reliability-preferences',
    ],
  },

  // Energy-Intensive Manufacturing Margin Intelligence — Materials sector
  // (Steel, Cement, Glass, Chemicals, Aluminum, Paper, Plastics).
  // Plant Margin Score, Energy Cost Impact, Production Optimization Ranking,
  // EBITDA Trending, Evidence Bundle generation for executive review.
  // Strictly Type-1: scoring, dashboards, recommendations, evidence delivery.
  'materials-margin-intelligence-v1': {
    llm_product: 'MaterialsMargin-LLM',
    displayName: 'Energy-Intensive Manufacturing Margin Intelligence.Type-1',
    db: 'pnp_materials_margin_v1',
    host: 'localhost',
    port: '5432',
    engine: 'PostgreSQL',
    user: 'pnp_readonly',
    tables: 22,
    description:
      'Energy-intensive manufacturing margin intelligence — plant EBITDA, energy cost impact, production optimization, multi-plant profitability scoring',
    rag_namespaces: [
      'materials-margin-intelligence-federal-policies',
      'materials-margin-intelligence-ceo-directives',
      'materials-margin-intelligence-lob-directives',
      'materials-margin-intelligence-preferences',
    ],
  },
};

/**
 * Resolve the effective database config for a client.
 * Priority: client's own DB > product_type default DB > null (demo)
 * Handles both canonical keys and URL-safe slugs automatically.
 */
export function resolveDbConfig(clientDbConfig, productType) {
  if (clientDbConfig) return { ...clientDbConfig, source: 'client_prod' };
  // Try direct lookup first, then slug→canonical resolution
  const canonicalKey = resolveProductType(productType) || productType;
  const productDb = PRODUCT_DB_MAP[canonicalKey];
  if (productDb) return { ...productDb, source: 'prod' };
  return null; // demo
}

/**
 * Get the dot indicator info for a client.
 */
export function getDbStatus(clientDbConfig, productType) {
  if (clientDbConfig) {
    return { color: '#2196F3', label: 'client_prod', tooltip: `Client DB: ${clientDbConfig.db} @ ${clientDbConfig.host}:${clientDbConfig.port || '5432'}` };
  }
  const canonicalKey = resolveProductType(productType) || productType;
  const productDb = PRODUCT_DB_MAP[canonicalKey];
  if (productDb) {
    return { color: '#4CAF50', label: 'prod', tooltip: `Prod: ${productDb.engine} • ${productDb.db} @ ${productDb.host}:${productDb.port} • ${productDb.tables} tables • ${productDb.description}` };
  }
  return { color: '#FF9800', label: 'demo', tooltip: 'Demo: No database configured — using hardcoded sample data' };
}

/**
 * Get the dot indicator for a product_type in the Products by Builders table.
 */
export function getProductTypeDbStatus(productType) {
  const db = PRODUCT_DB_MAP[productType];
  if (db) {
    return { color: '#4CAF50', label: 'prod', tooltip: `Prod: ${db.engine} • ${db.db} @ ${db.host}:${db.port} • ${db.tables} tables\n${db.description}` };
  }
  return { color: '#FF9800', label: 'demo', tooltip: 'Demo: No local database configured for this product type' };
}

/**
 * Reverse index: URL/path/namespace-safe slug → canonical ProductType key.
 *
 * Used at any boundary where input may arrive in slug form (REST path
 * segments, RAG namespace lookups, filesystem paths, Kafka topic suffixes).
 * Extend this map whenever a new product_type adds a slug.
 */
export const PRODUCT_SLUG_TO_CANONICAL = {
  [PRODUCT_TYPE_SLUG_TN_PROCUREMENT]: PRODUCT_TYPE_CANONICAL_TN_PROCUREMENT,
  [PRODUCT_TYPE_SLUG_CHINA_US_INTEL]: PRODUCT_TYPE_CANONICAL_CHINA_US_INTEL,
  [PRODUCT_TYPE_SLUG_INDUSTRIAL_WATER_PFAS]: PRODUCT_TYPE_CANONICAL_INDUSTRIAL_WATER_PFAS,
  // Type-2 display alias → canonical (frontend shows .Type-2, backend uses .Type-1)
  'IndustrialWater-PFAS-Intelligence.Type-2': PRODUCT_TYPE_CANONICAL_INDUSTRIAL_WATER_PFAS,
  'industrial-water-pfas-intelligence-type-2': PRODUCT_TYPE_CANONICAL_INDUSTRIAL_WATER_PFAS,
};

/**
 * Resolve either a canonical product_type key or its URL-safe slug to the
 * canonical key. Returns null if neither form is registered.
 *
 *   resolveProductType('Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)')
 *     → 'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)'
 *   resolveProductType('procurement-intelligence-type-1-tn-prototype')
 *     → 'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)'
 *   resolveProductType('Unknown.Type-1') → null
 *
 * Callers should treat the returned value as the canonical key and use it
 * for all downstream lookups in PRODUCT_DB_MAP, prompt injection, audit
 * logs, and UI rendering.
 */
export function resolveProductType(input) {
  if (input == null) return null;
  if (PRODUCT_DB_MAP[input]) return input;
  const canonical = PRODUCT_SLUG_TO_CANONICAL[input];
  return canonical || null;
}

/**
 * Get the URL/path/namespace-safe slug for a canonical product_type key.
 * Returns null if no slug is registered (most product types don't need one
 * because their keys are already URL-safe).
 */
export function getProductTypeSlug(productType) {
  const entry = PRODUCT_DB_MAP[productType];
  return entry && entry.slug ? entry.slug : null;
}

export default PRODUCT_DB_MAP;
